<template>
  <div class="pa-2 pb-10">
    <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs" />
    <heading>{{ folderName }}</heading>
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-text-field
          v-model="search"
          single-line
          hide-details
          dense
          clearable
          :label="$t('general.search')"
          style="max-width: 370px"
        />
        <v-spacer />
        <v-btn
          v-if="canManageStructureHere"
          color="secondary"
          outlined
          @click="newTemplate"
        >
          {{ $t("riskAssessment.templates.addTemplate") }}
          <v-icon right>add</v-icon>
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="templates"
        :search="search"
        :loading="loading"
        item-key="_id"
        class="elevation-0"
        :no-data-text="$t('general.noData')"
        :items-per-page="-1"
        :footer-props="{ itemsPerPageOptions: [-1, 10, 25, 50] }"
      >
        <template v-slot:item.type="{ item }">
          <v-chip
            x-small
            label
            :color="item.type === 'global' ? 'primary' : 'secondary'"
            dark
          >
            {{
              item.type === "global"
                ? $t("riskAssessment.templates.global")
                : $t("riskAssessment.templates.local")
            }}
          </v-chip>
        </template>

        <template v-slot:item.kind="{ item }">
          {{
            item.kind === "advanced"
              ? $t("riskAssessment.kind.advancedShort")
              : $t("riskAssessment.kind.basicShort")
          }}
        </template>

        <template v-slot:item.risksCount="{ item }">
          {{ item.risks ? item.risks.length : 0 }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn v-if="canCreateHere" icon @click="useTemplate(item)">
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditStructureHere"
            icon
            @click="openMoveDialog(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>content_paste_go</v-icon>
          </v-btn>
          <v-btn
            v-if="canEditStructureHere"
            icon
            @click="editTemplate(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="canManageStructureHere"
            icon
            @click="confirmDelete(item)"
            :disabled="!canEdit(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="kindChooserOpen" max-width="800px">
      <v-card>
        <v-list>
          <v-list-item>
            <v-btn block color="primary" @click="newTemplateOfKind('basic')">
              {{ $t("riskAssessment.kind.basic") }}
            </v-btn>
          </v-list-item>
          <v-list-item>
            <v-btn block color="primary" @click="newTemplateOfKind('advanced')">
              {{ $t("riskAssessment.kind.advanced") }}
            </v-btn>
          </v-list-item>
        </v-list>
      </v-card>
    </v-dialog>

    <templateTargetFolderDialog
      ref="folderDialog"
      loadAction="getAllRiskAssessmentCategories"
    />
    <moveDialog
      :item="moveItem"
      :moveDialog="moveDialog"
      :loading="moving"
      :array="moveTargets"
      @cancel="moveDialog.open = false"
      @confirm="handleMoveConfirm"
    />

    <v-dialog v-model="deleteDialog.open" max-width="400">
      <v-card>
        <v-card-title>{{
          $t("riskAssessment.templates.confirmDelete")
        }}</v-card-title>
        <v-card-text>
          {{
            $t("riskAssessment.templates.confirmDeleteText", {
              name: deleteDialog.item ? deleteDialog.item.name : "",
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog.open = false">{{
            $t("general.back")
          }}</v-btn>
          <v-btn color="error" text @click="deleteTemplate">{{
            $t("general.delete")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import i18n from "../../i18n";
import moveDialog from "../../components/_utils/dataTableHelpers/actions/moveDialog.vue";
import templateTargetFolderDialog from "../../components/_utils/templateTargetFolderDialog.vue";

export default {
  name: "riskAssessmentTemplateList",
  components: { moveDialog, templateTargetFolderDialog },
  data() {
    return {
      templates: [],
      folders: [],
      loading: false,
      search: "",
      isOwner: false,
      deleteDialog: { open: false, item: null },
      kindChooserOpen: false,
      moveItem: {},
      moving: false,
      moveTargets: [],
      moveDialog: {
        open: false,
        title: "riskAssessment.templates.folder.moveTitle",
        description: "",
      },
    };
  },
  computed: {
    categoryId() {
      return this.$route.params.templateCategoryId;
    },
    folderName() {
      return this.$route.params.templateCategoryName || "";
    },
    breadcrumbs() {
      return [
        {
          text: i18n.t("sidebar.riskAssessment"),
          disabled: false,
          href: "/app/riskAssessmentCategory",
        },
        {
          text: i18n.t("riskAssessment.templates.title"),
          disabled: false,
          href: "/app/riskAssessmentTemplates",
        },
        { text: this.folderName, disabled: true },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("riskAssessment.templates.name"),
          value: "name",
          align: "left",
        },
        { text: this.$t("riskAssessment.templates.type"), value: "type" },
        { text: this.$t("riskAssessment.tableHeader.kind"), value: "kind" },
        {
          text: this.$t("riskAssessment.templates.risksCount"),
          value: "risksCount",
          sortable: false,
        },
        { text: "", value: "actions", align: "right", sortable: false },
      ];
    },
  },
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.isOwner = user && isMaster(user.email);
    const foldersRes = await this.$store.dispatch(
      "getRiskAssessmentTemplateCategories",
    );
    this.folders = (foldersRes && foldersRes.data) || [];
    this.loadTemplates();
  },
  methods: {
    async useTemplate(item) {
      let target = this.$route.query.targetId
        ? { _id: this.$route.query.targetId, name: this.$route.query.targetName }
        : null;
      if (!target) target = await this.$refs.folderDialog.pick();
      if (!target) return;
      this.$router.push({
        name: "riskAssessmentForm",
        params: {
          riskAssessmentCategoryId: target._id,
          riskAssessmentCategoryName: target.name || "-",
          id: "1",
          mode: "new",
        },
        query: {
          templateId: item._id,
          ...(item.kind === "advanced" ? { kind: "advanced" } : {}),
        },
      });
    },
    openMoveDialog(item) {
      this.moveItem = item;
      const targets = [
        {
          id: "none",
          name: this.$t("riskAssessment.templates.folder.ohneOrdner"),
        },
      ];
      this.folders
        .filter((f) => f.type === item.type)
        .forEach((f) => targets.push({ id: f._id, name: f.name }));
      this.moveTargets = targets.filter((t) => t.id !== this.categoryId);
      this.moveDialog.description = this.$t(
        "riskAssessment.templates.folder.moveText",
        { name: item.name },
      );
      this.moveDialog.open = true;
    },
    async handleMoveConfirm(targetId) {
      try {
        this.moving = true;
        await this.$store.dispatch("updateRiskAssessmentTemplate", {
          _id: this.moveItem._id,
          category: targetId === "none" ? null : targetId,
        });
        this.moveDialog.open = false;
        this.loadTemplates();
      } finally {
        this.moving = false;
      }
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const res = await this.$store.dispatch("getRiskAssessmentTemplates", {
          category: this.categoryId,
        });
        this.templates = (res && res.data) || [];
      } finally {
        this.loading = false;
      }
    },
    canEdit(item) {
      if (this.isOwner) return true;
      return item.type === "local";
    },
    newTemplate() {
      this.kindChooserOpen = true;
    },
    newTemplateOfKind(kind) {
      this.kindChooserOpen = false;
      this.$router.push({
        name: "riskAssessmentTemplateForm",
        params: {
          id: "1",
          mode: "new",
          templateCategoryId: this.categoryId,
          templateCategoryName: this.folderName,
        },
        query: kind === "advanced" ? { kind } : {},
      });
    },
    editTemplate(item) {
      this.$router.push({
        name: "riskAssessmentTemplateForm",
        params: { id: item._id, mode: "edit" },
      });
    },
    confirmDelete(item) {
      this.deleteDialog.item = item;
      this.deleteDialog.open = true;
    },
    async deleteTemplate() {
      if (!this.deleteDialog.item) return;
      await this.$store.dispatch(
        "deleteRiskAssessmentTemplate",
        this.deleteDialog.item._id,
      );
      this.deleteDialog.open = false;
      this.deleteDialog.item = null;
      this.loadTemplates();
    },
  },
};
</script>
